import React, { CSSProperties, useEffect, useRef, useState } from "react";
import { useWorkflowsStore } from "../stores/workflows";
import { useEleventEventStore } from "../stores/elementEvent";
import { cn } from "../utils";
import { Mention, MentionInput } from "./ui/mention";
import { IconTick, IconAi, CarretDown, IconMinus } from "../Icons";

interface MentionChatProps {
  handleInputChange?: (value: string) => void;
  onSubmit?: (event: React.UIEvent | React.FormEvent) => void;
  style?: CSSProperties;
  onFileChange?: (files: FileList | undefined) => void;
  selectedElements?: HTMLElement[];
}

export const MentionChat: React.FC<MentionChatProps> = ({
  onSubmit,
  handleInputChange,
  selectedElements = [],
}) => {
  const mentionInputRef = useRef<HTMLInputElement>(null);
  const [values, setValues] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const workflows = useWorkflowsStore.getState().workflows || [];
  const selectedWorkflow = useWorkflowsStore((state) => state.workflowId);
  const { selectedAction, selectedEvent, showEventForm } = useEleventEventStore((state) => state);
  const { setSelectedAction, setSelectedEvent, setShowEventForm } = useEleventEventStore.getState();

  // Helper function to check if any selected element is an input, textarea, or button
  const hasValidSelectedElement = React.useMemo(() => {
    if (!selectedElements.length) return false;

    return selectedElements.some((element) => {
      const tagName = element.tagName.toLowerCase();
      return (
        tagName === "input" || tagName === "textarea" || tagName === "button"
      );
    });
  }, [selectedElements]);

  // Helper function to get available events for the selected element
  const getAvailableEvents = React.useMemo(() => {
    if (!selectedElements.length) return ["click"];

    // Get the first valid element
    const validElement = selectedElements.find((element) => {
      const tagName = element.tagName.toLowerCase();
      return (
        tagName === "input" || tagName === "textarea" || tagName === "button"
      );
    });

    if (!validElement) return ["click"];

    const tagName = validElement.tagName.toLowerCase();
    if (tagName === "button") {
      return ["onClick"];
    } else if (tagName === "input" || tagName === "textarea") {
      return [
        "onChange",
        "onFocus",
        "onBlur",
        "onKeyDown",
        "onKeyUp",
        "onPaste",
      ];
    }

    return ["click"];
  }, [selectedElements]);

  useEffect(() => {
    handleInputChange?.(inputValue);
    if (!inputValue.trim()) {
      setValues([]);
      return;
    }
  }, [inputValue]);

  useEffect(() => {
    if (!mentionInputRef.current) return;
    mentionInputRef.current.parentElement
      ?.querySelector("div")
      ?.classList?.add("!whitespace-nowrap");
  }, [mentionInputRef.current]);

  // Reset selectedAction when event selection changes
  useEffect(() => {
    if (!selectedEvent) {
      setSelectedAction("");
    }
  }, [selectedEvent]);

  const toggleEventForm = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setShowEventForm(!showEventForm);
  };

  // Function to clear the form and hide it
  const clearAndHideForm = () => {
    setInputValue("");
    setSelectedEvent("");
    setSelectedAction("");
    setShowEventForm(false);
    useWorkflowsStore.getState().setWorkflowId("");
    handleWorkflowChange({ target: { value: "" } } as React.ChangeEvent<HTMLSelectElement>);
  };

  // Add a handler for workflow selection
  const handleWorkflowChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const workflowId = e.target.value;
    useWorkflowsStore.getState().setWorkflowId(workflowId);
    window.parent.postMessage(
      {
        type: "ELEMENT_INSPECTOR_MENTIONED",
        payload: { workflowId },
      },
      "*",
    );
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSubmit(e);
    }
  };

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (inputValue.trim() === "") return;
    onSubmit?.(e as React.UIEvent | React.FormEvent);
    clearAndHideForm();
  }

  return (
    <Mention
      trigger="/"
      className={cn(
        "text-md text-bolt-elements-textPrimary placeholder-bolt-elements-textTertiary w-full resize-none bg-transparent focus:outline-none",
        "element-inspector-mention",
      )}
      inputValue={inputValue}
      value={values}
      onInputValueChange={setInputValue}
      onValueChange={setValues}
    >
      <div className={`relative flex items-center gap-1`}>
        <div className="flex h-6 w-6 items-center justify-center">
          <IconAi />
        </div>
        <MentionInput
          id="mention-chat-input"
          onKeyDown={handleInputKeyDown}
          value={inputValue}
          ref={mentionInputRef}
          placeholder={"Tell me how to modify this element..."}
          className={cn(
            "text-bolt-elements-textPrimary placeholder-bolt-elements-textTertiary w-full min-w-[300px] !border-none bg-transparent px-0 pr-[40px] text-sm !shadow-none !ring-0 focus:outline-none",
          )}
        />
        {inputValue.trim() !== "" && (
          <button
            type="button"
            onClick={handleSubmit}
            className="absolute right-0 flex items-center justify-center rounded-full bg-[#3b82f6] p-1 text-white transition hover:bg-[#2563eb]"
          >
            <IconTick />
          </button>
        )}
      </div>
      {hasValidSelectedElement &&
        (!showEventForm ? (
          <button
            onClick={toggleEventForm}
            className="inline-flex cursor-pointer items-center border-none bg-transparent text-xs text-blue-500 underline transition-colors hover:text-blue-700 focus:ring-0 focus:outline-none"
          >
            Add event
          </button>
        ) : (
          <div
            className="edit-event-handler relative mt-3 border-none bg-white outline-none"
            style={{ zIndex: 10002 }}
          >
            <div className="relative mb-6">
              <button
                onClick={clearAndHideForm}
                className="absolute top-0 right-0 cursor-pointer rounded-full border border-red-400 bg-white text-red-400 transition-colors duration-200 hover:border-red-700 hover:text-red-700 focus:outline-none"
              >
                <IconMinus />
              </button>
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Event
              </label>
              <div className="relative">
                <select
                  className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
                  onChange={(e) => setSelectedEvent(e.target.value)}
                  value={selectedEvent}
                >
                  <option value="">Select an event</option>
                  {getAvailableEvents.map((event) => (
                    <option key={event} value={event}>
                      {event}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <CarretDown />
                </div>
              </div>
            </div>

            <div className={selectedAction ? `mb-6` : ""}>
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Action
              </label>
              <div className="relative">
                <select
                  className={`block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none ${!selectedEvent ? "cursor-not-allowed opacity-50" : ""}`}
                  onChange={(e) => setSelectedAction(e.target.value)}
                  value={selectedAction}
                  disabled={!selectedEvent}
                >
                  <option value="">Select an action</option>
                  <option value="triggerWorkflow">Trigger workflow</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <CarretDown />
                </div>
              </div>
            </div>

            {selectedAction === "triggerWorkflow" && (
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Workflow
                </label>
                <div className="relative">
                  <select
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
                    onChange={handleWorkflowChange}
                    value={selectedWorkflow}
                  >
                    <option value="">Select a workflow</option>
                    {workflows.map((workflow) => (
                      <option key={workflow.id} value={workflow.id}>
                        {workflow.name}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <CarretDown />
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
    </Mention>
  );
};
